import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Entitled } from '@/components/Entitled';
import { seedExportConfig } from '@/components/export/configs/seed.export.config';
import {
  type SeedKey,
  UniversalExportModal,
} from '@/components/export/universal';
import { Loader } from '@/components/Loader';
import { NoteModal } from '@/components/modals';
import { useEntitlements } from '@/hooks/useEntitlements';
import { SeedDrawerContent } from '@/sections/seeds/components/drawer/SeedDrawerContent';
import { SeedDrawerHeader } from '@/sections/seeds/components/drawer/SeedDrawerHeader';
import { SeedDrawerTabs } from '@/sections/seeds/components/drawer/SeedDrawerTabs';
import { SeedRemoveTag } from '@/sections/seeds/components/modals/SeedRemoveTag';
import { SeedTag } from '@/sections/seeds/components/modals/SeedTag';
import { SeedsActions } from '@/sections/seeds/components/SeedsActions';
import {
  useSeedDrawerData,
  useSeedDrawerTab,
} from '@/sections/seeds/hooks/drawer';
import { useSeedsActions } from '@/sections/seeds/hooks/useSeedsActions';
import { useGlobalState } from '@/state/global.state';

/**
 * Seed Drawer Component
 *
 * KEY-BASED PATTERN: Uses seedDrawerKey from URL params as single source of truth.
 * The hook fetches seed data internally, eliminating race conditions from
 * dual state sources (synchronous setSeed vs async API response).
 *
 * Refactoring Note: Updated to key-based pattern (2025-12-08)
 * - Removed dependency on drawerState.seed (race condition source)
 * - Uses seedDrawerKey from URL params as single source of truth
 * - Hook fetches seed data internally via seedDrawerKey
 * - Following AssetDrawer pattern for consistent drawer architecture
 *
 * Previous updates (2025-10-24):
 * - Added useSeedsActions coordinator hook for unified state management
 * - Added tag management modals (AddTag, RemoveTag)
 * - Added export modal support
 * - Added SeedsActions toolbar for drawer actions
 * - Following Technology drawer pattern
 */

export function SeedDrawerComponent() {
  const { drawerState } = useGlobalState();
  const queryClient = useQueryClient();
  const { canWriteAssets } = useEntitlements();

  // LOCAL TAB STATE: Use local state with debounced URL sync to prevent
  // tab alternation bug during rapid arrow key navigation
  const [currentTab, setCurrentTab] = useSeedDrawerTab();

  // KEY-BASED PATTERN: Use seedDrawerKey from URL params as single source of truth
  const seedDrawerKey = drawerState.seedDrawerKey || '';

  // Coordinated data fetching for drawer - seed is fetched internally by key
  const {
    seed,
    displayDetails,
    history,
    originationInfo,
    accesss,
    detailsStatus,
    historyStatus,
    isInitialLoading,
  } = useSeedDrawerData(seedDrawerKey);

  // Coordinator hook - composes modal state + action handlers
  const { modalState, modalHandlers, handlers } = useSeedsActions({
    selectedSeeds: seed ? [seed] : [],
    selectedRowsCount: seed ? 1 : 0,
    invalidateSeeds: () => {
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'seed';
        },
        refetchType: 'active',
      });
    },
    setConfirmStatus: () => {},
  });

  const [removeInitialTag, setRemoveInitialTag] = useState<
    string | undefined
  >();

  const handleComment = async (comment: string) => {
    await handlers.handleComment(comment);
  };

  // Show loading state while seed is being fetched
  if (isInitialLoading) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Loader />
      </div>
    );
  }

  // NOW safe to return early - all hooks have been called
  if (!seed) return null;

  // Get current active tab value with seed-type-aware default
  // FIX ENG-138: Webapplication seeds don't have 'details' tab, they start with 'authentication'
  const defaultTab =
    seed.class === 'webapplication' ? 'authentication' : 'details';
  const currentTabValue = currentTab || defaultTab;

  return (
    <div
      className="flex size-full flex-col gap-6 p-6"
      data-testid="seed-details"
    >
      <SeedDrawerHeader
        seed={seed}
        onRemoveTag={tag => {
          setRemoveInitialTag(tag);
          modalHandlers.openRemoveTagModal();
        }}
      />

      {/* Actions toolbar for drawer */}
      <Entitled entitlement="write_assets">
        <SeedsActions
          seedType={
            seed.class === 'webapplication'
              ? 'webapplication'
              : seed.class === 'ipv4' ||
                  seed.class === 'ipv6' ||
                  seed.class === 'cidr'
                ? 'ip'
                : seed.class === 'preseed'
                  ? 'preseed'
                  : 'domain'
          }
          selectedSeeds={seed ? [seed] : []}
          selectedRowsCount={seed ? 1 : 0}
          modalHandlers={modalHandlers}
          actionHandlers={handlers}
          onClearSelection={() => {}}
          hideAddSeed={true}
        />
      </Entitled>

      <div className="bg-chariot-stroke h-px w-full shrink-0"></div>

      <SeedDrawerTabs
        seed={seed}
        activeTab={currentTabValue}
        onTabChange={setCurrentTab}
      />
      <div className="size-full overflow-y-auto">
        <SeedDrawerContent
          seed={seed}
          activeTab={currentTabValue}
          displayDetails={displayDetails}
          history={history}
          originationInfo={originationInfo}
          accesss={accesss}
          detailsStatus={detailsStatus}
          historyStatus={historyStatus}
          onCommentClick={
            canWriteAssets
              ? () => {
                  modalHandlers.openCommentModal();
                }
              : undefined
          }
        />
      </div>

      {/* Modals */}
      <NoteModal
        open={modalState.comment.isOpen}
        onClose={modalHandlers.closeCommentModal}
        onSubmit={handleComment}
        resourceType="Seed"
        mode="simple"
        selectedCount={1}
        resource={{ key: seed.key, name: seed.identifier }}
        presentation="modal"
      />

      <SeedTag
        open={modalState.tag.isOpen}
        onClose={modalHandlers.closeTagModal}
        seedKeys={seed ? [seed.key] : []}
        onSuccess={() => {
          queryClient.invalidateQueries({
            predicate: query => {
              const queryKey = query.queryKey as string[];
              return queryKey[0] === 'MY' && queryKey[1] === 'seed';
            },
            refetchType: 'active',
          });
        }}
      />

      <SeedRemoveTag
        open={modalState.removeTag.isOpen}
        onClose={() => {
          setRemoveInitialTag(undefined);
          modalHandlers.closeRemoveTagModal();
        }}
        seedKeys={seed ? [seed.key] : []}
        initialTag={removeInitialTag}
        onSuccess={() => {
          setRemoveInitialTag(undefined);
          queryClient.invalidateQueries({
            predicate: query => {
              const queryKey = query.queryKey as string[];
              return queryKey[0] === 'MY' && queryKey[1] === 'seed';
            },
            refetchType: 'active',
          });
        }}
      />

      <UniversalExportModal
        config={seedExportConfig}
        open={modalState.export.isOpen}
        onClose={modalHandlers.closeExportModal}
        selectedRows={
          seed?.key ? ([seed.key] as unknown as readonly SeedKey[]) : []
        }
        onClearSelection={modalHandlers.closeExportModal}
      />
    </div>
  );
}
